import { useEffect, useRef } from 'react'
import { Toaster } from 'sonner'
import { NewsLayout, Sidebar, MainContent } from '@/features/news/components'
import {
  useSearchOrchestration,
  useAutoSearch,
  useKeywordManagement,
  useAuthModal,
  useKeywordServerSync,
} from '@/features/news/hooks'
import { useKeywordNews } from '@/features/news/hooks/useNewsSearch'
import { useStreamingSummary } from '@/features/news/hooks/useStreamingSummary'
import { useNewsStore } from '@/features/news/stores/newsStore'
import { useInitializeAuth, AuthModal } from '@/features/auth'
import { useDocumentMeta } from '@/lib/seo'

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// App Component
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

function App() {
  // Initialize Firebase auth listener
  useInitializeAuth()

  // State management
  const { keywords: storeKeywords, activeKeyword, tabs, settings, setTabPage } = useNewsStore()

  // Custom hooks
  const { handleSearch, isPending } = useSearchOrchestration()
  const {
    registeredKeywords,
    searchErrors,
    handleRegisteredKeywordsChange,
    clearErrors,
    setRegisteredKeywords,
    validateKeywords,
  } = useKeywordManagement()
  const { showAuthModal, setShowAuthModal, openAuthModal } = useAuthModal()

  // Pagination hook. Keyword and page are passed per call, not captured here.
  const { mutate: fetchPage } = useKeywordNews({
    display: settings.display,
    sort: settings.sort,
  })

  // AI Summary streaming hook.
  // Owned here and passed to MainContent — the hook holds its state locally, so a
  // second call site would never see these summaries.
  const streaming = useStreamingSummary()
  const { startStreaming, isStreaming } = streaming

  // Track which keywords have been summarized to prevent infinite loops
  const summarizedKeywordsRef = useRef<Set<string>>(new Set())

  // Auto-search on mount (fresh defaults, or the keywords restored from storage)
  useAutoSearch(registeredKeywords)

  // Keep the digest's copy of the keywords in step with this device (P3-20). Sits
  // here because this is where the keywords live — every editor in the sidebar
  // reports through `handleRegisteredKeywordsChange`, so one call covers all of them.
  useKeywordServerSync(registeredKeywords)

  // Generate AI summary when active keyword tab has data and AI summary is enabled
  useEffect(() => {
    if (!settings.enableAISummary || !activeKeyword) return

    const currentTab = tabs[activeKeyword]
    if (!currentTab?.items || currentTab.items.length === 0) return

    // Skip if already streaming or already summarized this keyword
    if (isStreaming(activeKeyword) || summarizedKeywordsRef.current.has(activeKeyword)) return

    // Mark as summarized and start streaming
    summarizedKeywordsRef.current.add(activeKeyword)
    startStreaming({
      keyword: activeKeyword,
      articles: currentTab.items,
    })
  }, [activeKeyword, settings.enableAISummary, isStreaming, startStreaming, tabs])

  // Clear summarized keywords when performing new search
  useEffect(() => {
    if (isPending) {
      summarizedKeywordsRef.current.clear()
    }
  }, [isPending])

  // SEO: Update document meta tags based on active keyword
  useDocumentMeta({
    title: activeKeyword
      ? `'${activeKeyword}' 뉴스 검색 결과`
      : storeKeywords.length > 0
        ? `${storeKeywords.join(', ')} 뉴스 검색`
        : undefined,
    description: activeKeyword
      ? `${activeKeyword} 관련 최신 네이버 뉴스 ${tabs[activeKeyword]?.total || 0}건을 실시간으로 확인하세요`
      : `최대 20개 키워드를 동시에 검색하고 실시간으로 뉴스를 모니터링하세요`,
    keywords: storeKeywords.length > 0 ? storeKeywords : undefined,
    ogUrl:
      storeKeywords.length > 0
        ? `https://headliner.kr/?keywords=${encodeURIComponent(storeKeywords.join(','))}`
        : undefined,
  })

  // Event handlers
  const handleSearchClick = () => {
    clearErrors()
    if (!validateKeywords()) return
    handleSearch(registeredKeywords)
  }

  const handlePresetSelect = (keywords: string[]) => {
    setRegisteredKeywords(keywords)
    clearErrors()
    handleSearch(keywords, { shouldSetActiveKeyword: true })
  }

  const handlePageChange = (keyword: string, page: number) => {
    setTabPage(keyword, page)
    fetchPage({ keyword, page })
  }

  const handleSelectHistory = (keywords: string[], params: { display: number; sort: string }) => {
    setRegisteredKeywords(keywords)
    clearErrors()
    handleSearch(keywords, {
      display: params.display,
      sort: params.sort as 'sim' | 'date',
      shouldSetActiveKeyword: true,
    })
  }

  const handleSearchGroups = (keywords: string[]) => {
    clearErrors()
    if (keywords.length === 0) {
      return
    }
    setRegisteredKeywords(keywords)
    handleSearch(keywords, { shouldSetActiveKeyword: true })
  }

  return (
    <>
      <Toaster position="top-right" richColors closeButton />
      <NewsLayout
        sidebar={
          <Sidebar
            registeredKeywords={registeredKeywords}
            onRegisteredKeywordsChange={handleRegisteredKeywordsChange}
            searchErrors={searchErrors}
            isPending={isPending}
            onSearch={handleSearchClick}
            onSearchGroups={handleSearchGroups}
            onSelectHistory={handleSelectHistory}
            onOpenAuthModal={openAuthModal}
            onSelectPreset={handlePresetSelect}
          />
        }
        main={
          <MainContent
            onPresetSelect={handlePresetSelect}
            onPageChange={handlePageChange}
            streaming={streaming}
          />
        }
      />

      {/* Auth Modal */}
      <AuthModal open={showAuthModal} onOpenChange={setShowAuthModal} />
    </>
  )
}

export default App
